import React from "react";
import {
  Bar,
  BarChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ClicksBreakdown,
  ClicksOverviewResponse,
  ClicksOverviewSegment,
} from "../../api/analyticsAdmin";
import { formatRate, segmentLabel } from "../../helpers/searchAnalytics";
import { formatInteger } from "../../helpers/formatNumber";
import SectionBody from "./SectionBody";
import { ClicksMetricHeader } from "./clicksMetricHeaders";
import { SectionState } from "./useAnalyticsStream";
import { sortSegments } from "../../helpers/analyticsPlus";
import { CategoryBarTick, SEGMENT_ROW_HEIGHT } from "./activityCharts";

const RATE_SERIES: { key: keyof ClicksOverviewSegment; label: string; color: string }[] = [
  { key: "openToOutboundRate", label: "Open → outbound", color: "rgb(109 76 145 / 0.9)" },
  { key: "openToConversionRate", label: "Open → conversion", color: "rgb(139 92 246 / 0.7)" },
];

// Raw Source segment keys from the API mapped to human-readable labels.
const SOURCE_LABELS: Record<string, string> = {
  search: "Search",
  section: "Home",
  featured_offers: "Featured",
  category: "Categories",
  deeplink: "Via deeplink",
};

const sourceLabel = (name: string): string =>
  SOURCE_LABELS[name] ?? segmentLabel(name);

// The Offer type breakdown's "unknown" bucket means the user had no offer-type
// filter applied, so label it "No Filter" rather than the generic "Unknown".
const offerTypeLabel = (name: string): string =>
  name === "unknown" ? "No Filter" : segmentLabel(name);

const SAMPLE_THRESHOLD = 10;

const SegmentChart: React.FC<{
  segments: ClicksOverviewSegment[];
  labelFn?: (name: string) => string;
}> = ({ segments, labelFn = segmentLabel }) => (
  <div className="bg-white rounded-lg shadow p-6 mb-4">
    <div style={{ height: Math.max(160, segments.length * SEGMENT_ROW_HEIGHT) + 8 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          layout="vertical"
          data={segments}
          margin={{ top: 24, right: 24, left: 8, bottom: 5 }}
          barGap={2}
          barCategoryGap="35%"
        >
          <XAxis
            type="number"
            domain={[0, 1]}
            tickFormatter={(value: number) => formatRate(value)}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={4}
            tickLine={false}
            interval={0}
            tick={(props) => <CategoryBarTick {...props} labelFn={labelFn} />}
          />
          <Tooltip formatter={(value: number) => formatRate(value)} />
          <Legend />
          {RATE_SERIES.map((s) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label}
              fill={s.color}
              radius={[0, 4, 4, 0]}
              maxBarSize={18}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const SegmentTable: React.FC<{
  segments: ClicksOverviewSegment[];
  labelFn?: (name: string) => string;
}> = ({ segments, labelFn = segmentLabel }) => (
  <div className="bg-white rounded-lg shadow overflow-hidden">
    <table className="w-full text-sm">
      <thead className="bg-gray-50 text-gray-500">
        <tr>
          <th className="text-left font-medium px-4 py-3">Segment</th>
          <ClicksMetricHeader metric="opens" />
          <ClicksMetricHeader metric="outbounds" />
          <ClicksMetricHeader metric="openers" />
          <ClicksMetricHeader metric="converted" />
          <ClicksMetricHeader metric="openToOutbound" />
          <ClicksMetricHeader metric="openToConversion" />
        </tr>
      </thead>
      <tbody>
        {segments.map((s) => (
          <tr
            key={s.name}
            className={`border-t border-gray-100 ${
              s.opens < SAMPLE_THRESHOLD ? "text-gray-400" : ""
            }`}
          >
            <td className="px-4 py-3">{labelFn(s.name)}</td>
            <td className="px-4 py-3 text-right">{formatInteger(s.opens)}</td>
            <td className="px-4 py-3 text-right">{formatInteger(s.outbounds)}</td>
            <td className="px-4 py-3 text-right">{formatInteger(s.uniqueOpeners)}</td>
            <td className="px-4 py-3 text-right">{formatInteger(s.convertedPairs)}</td>
            <td className="px-4 py-3 text-right">{formatRate(s.openToOutboundRate)}</td>
            <td className="px-4 py-3 text-right">{formatRate(s.openToConversionRate)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const labelFnFor = (breakdown: ClicksBreakdown) =>
  breakdown === "Source"
    ? sourceLabel
    : breakdown === "OfferType"
    ? offerTypeLabel
    : segmentLabel;

export interface ClicksBreakdownSection {
  key: ClicksBreakdown;
  label: string;
  section: SectionState<ClicksOverviewResponse>;
}

/**
 * The headline funnel and its six breakdowns, each settling on its own.
 *
 * They used to be seven requests behind one Promise.all and one loading flag, so
 * a single breakdown timing out replaced the headline and the other five with
 * "Failed to load clicks funnel." Now the headline renders as soon as it lands
 * and a failed breakdown says so in its own place.
 */
const ClicksFunnelOverview: React.FC<{
  headline: SectionState<ClicksOverviewResponse>;
  breakdowns: ClicksBreakdownSection[];
}> = ({ headline, breakdowns }) => (
  <section className="mb-12">
    <h3 className="text-lg font-medium mb-4">Clicks funnel</h3>

    <SectionBody
      section={headline}
      isEmpty={(data) => !data.segments[0] || data.segments[0].opens === 0}
      emptyMessage="No opens for this range."
    >
      {(data) => {
        const totals = data.segments[0];
        return (
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            {[
              {
                label: "Opens",
                pct: 1,
                value: "100%",
                note: `${formatInteger(totals.opens)} opens`,
              },
              {
                label: "Outbound clicks",
                pct: totals.openToOutboundRate,
                value: formatRate(totals.openToOutboundRate),
                note: "of opens led to an outbound",
              },
              {
                label: "Conversions",
                pct: totals.openToConversionRate,
                value: formatRate(totals.openToConversionRate),
                note: "of opened businesses converted",
              },
            ].map((stage) => (
              <div key={stage.label} className="flex items-center gap-3">
                <div className="w-40">
                  <div className="text-sm text-gray-600">{stage.label}</div>
                  <div className="text-xs text-gray-400">{stage.note}</div>
                </div>
                <div className="flex-1 bg-gray-100 rounded h-8 overflow-hidden">
                  <div
                    className="bg-primary h-8 rounded flex items-center justify-center"
                    style={{ width: `${Math.round(stage.pct * 100)}%` }}
                  >
                    <span className="text-sm font-medium text-white whitespace-nowrap">
                      {stage.value}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            <div className="text-xs text-gray-500 pt-2">
              {formatInteger(totals.uniqueOpeners)} unique openers ·{" "}
              {formatInteger(totals.openedPairs)} opened (user, business) pairs
            </div>
          </div>
        );
      }}
    </SectionBody>

    <div className="mt-10">
      <h3 className="text-lg font-medium mb-4">Breakdown</h3>
      <div className="space-y-8">
        {breakdowns.map((breakdown) => (
          <div key={breakdown.key}>
            <h4 className="text-sm font-medium text-gray-600 mb-2">
              {breakdown.label}
            </h4>
            <SectionBody
              section={breakdown.section}
              isEmpty={(data) => data.segments.length === 0}
              emptyMessage="No data for this range."
            >
              {(data) => {
                const segments = sortSegments(
                  breakdown.key,
                  data.segments,
                  (s) => s.name,
                  (s) => s.opens
                );
                const labelFn = labelFnFor(breakdown.key);
                return (
                  <>
                    <SegmentChart segments={segments} labelFn={labelFn} />
                    <SegmentTable segments={segments} labelFn={labelFn} />
                  </>
                );
              }}
            </SectionBody>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default ClicksFunnelOverview;
